import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { BuyingPointComparisonRow } from '../threeMonth.types';
import { FORECASTING_THEME } from '../forecastingTheme';
import { formatCurrency } from '../utils/forecastingAggregations';
import { ChartPanel, ChartPlot, ChartTitle, Legend } from '../Forecasting.styles';
import { currencyAxisProps, maxOf } from '../utils/currencyAxis';
import { useShellPalette } from '@/styles';

interface BuyingPointComparisonChartProps {
  data: BuyingPointComparisonRow[];
  metricLabel: string;
  title?: string;
}

export const BuyingPointComparisonChart: FC<BuyingPointComparisonChartProps> = ({
  data,
  metricLabel,
  title
}) => {
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();

  return (
    <ChartPanel>
      <ChartTitle>{title ?? `Buying Point — ${metricLabel}`}</ChartTitle>
      <Legend>
        <span>
          <i style={{ background: FORECASTING_THEME.booked }} />
          Dynamic Forecast {metricLabel}
        </span>
        <span>
          <i style={{ background: FORECASTING_THEME.pipe }} />
          Target {metricLabel}
        </span>
      </Legend>
      <ChartPlot>
        <ResponsiveContainer width="100%" height={280} minWidth={320}>
          <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 48 }} accessibilityLayer={false}>
            <CartesianGrid stroke={shell.chartGrid} vertical={false} />
            <XAxis
              dataKey="name"
              angle={-35}
              textAnchor="end"
              height={64}
              tick={{ fill: shell.dataText, fontSize: 9, fontWeight: 400 }}
              axisLine={{ stroke: shell.chartAxisLine }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              {...currencyAxisProps(maxOf(data, ['dynamicForecast', 'target']))}
              width={52}
            />
            <Tooltip
              formatter={(value: number) => formatCurrency(value)}
              contentStyle={{ background: '#fff', borderRadius: 4, fontSize: 12 }}
            />
            <Bar
              dataKey="dynamicForecast"
              name="Dynamic Forecast"
              fill={FORECASTING_THEME.booked}
              radius={[2, 2, 0, 0]}
              maxBarSize={28}
            />
            <Bar
              dataKey="target"
              name="Target"
              fill={FORECASTING_THEME.pipe}
              radius={[2, 2, 0, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartPlot>
    </ChartPanel>
  );
};
